// Use full URLs with environment variable support for API URL
const BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' 
    ? 'https://api.http.ly' // Replace with your production API URL
    : 'http://localhost:8787'
);

// Helper function to handle API responses
async function handleResponse(response) {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  const data = await response.json().catch(() => null);
  return data || [];
}

const api = {
  // Services
  async getServices() {
    const response = await fetch(`${BASE_URL}/services`);
    return handleResponse(response);
  },

  async createService(data) {
    const response = await fetch(`${BASE_URL}/services`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async updateService(id, data) {
    const response = await fetch(`${BASE_URL}/services/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async deleteService(id) {
    const response = await fetch(`${BASE_URL}/services/${id}`, { method: 'DELETE' });
    await handleResponse(response);
  },

  // Service Operations
  async getServiceOperations(serviceId) {
    const url = `${BASE_URL}/services/${serviceId}/operations`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  async createServiceOperation(serviceId, data) {
    const response = await fetch(`${BASE_URL}/services/${serviceId}/operations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async updateServiceOperation(serviceId, operationId, data) {
    const response = await fetch(`${BASE_URL}/services/${serviceId}/operations/${operationId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async deleteServiceOperation(serviceId, operationId) {
    const response = await fetch(`${BASE_URL}/services/${serviceId}/operations/${operationId}`, {
      method: 'DELETE',
    });
    await handleResponse(response);
  },

  // Service Configs
  async getServiceConfigs(serviceId) {
    const response = await fetch(`${BASE_URL}/services/${serviceId}/configs`);
    return handleResponse(response);
  },

  async createServiceConfig(serviceId, data) {
    const response = await fetch(`${BASE_URL}/services/${serviceId}/configs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async updateServiceConfig(serviceId, configId, data) {
    const response = await fetch(`${BASE_URL}/services/${serviceId}/configs/${configId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async deleteServiceConfig(serviceId, configId) {
    const response = await fetch(`${BASE_URL}/services/${serviceId}/configs/${configId}`, {
      method: 'DELETE',
    });
    await handleResponse(response);
  },

  // Endpoints
  async getEndpoints() {
    const response = await fetch(`${BASE_URL}/endpoints`);
    return handleResponse(response);
  },

  async createEndpoint(data) {
    // Ensure all required fields are present
    const payload = {
      name: data.name,
      description: data.description || '',
      projectId: data.projectId || 'default',
      serviceId: data.serviceId,
      operationId: data.operationId,
      method: data.method,
      path: data.path
    };
    
    const response = await fetch(`${BASE_URL}/endpoints`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  async updateEndpoint(id, data) {
    // Ensure all required fields are present
    const payload = {
      name: data.name,
      description: data.description || '',
      projectId: data.projectId || 'default',
      serviceId: data.serviceId,
      operationId: data.operationId,
      method: data.method,
      path: data.path
    };

    const response = await fetch(`${BASE_URL}/endpoints/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  async deleteEndpoint(id) {
    const response = await fetch(`${BASE_URL}/endpoints/${id}`, { method: 'DELETE' });
    await handleResponse(response);
  },

  // Requests
  async getRequests() {
    const response = await fetch(`${BASE_URL}/requests`);
    return handleResponse(response);
  },

  async createRequest(data) {
    const response = await fetch(`${BASE_URL}/requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async updateRequest(id, data) {
    const response = await fetch(`${BASE_URL}/requests/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async deleteRequest(id) {
    const response = await fetch(`${BASE_URL}/requests/${id}`, { method: 'DELETE' });
    await handleResponse(response);
  },
};

export { api };
